<template>
  <TabsContainer id="fr-details-container"
    :class="[
      'framework-details',
      tabContainerClassesStr,
      showProjGradDisclaimer?'is-proj-grad':'',
      isUpdatingLayout?'updating-layout':''
    ]"
    :is-calculating-layout="isUpdatingLayout"
    :input-tab-index="selectedTabIndex"
    tab-list-class="shadowed-bar no-print"
    tab-list-label="School Details"
    :tab-button-ids="tabButtonIds"
    :tab-content-ids="tabContentIds"
    :tab-button-classes="tabButtonClasses"
    :tab-content-classes="tabContentClasses"
    @tab-selected="onTabSelected"
    >
    <template v-for="(c,i) in tabCodes">
      <template :slot="`tab-button-${i}`">
        <span v-for="j in 2" :key="`btn-${i}-${j}`" class="tab-button-label">{{tabButtonLabelForTabCodeLine(c,j)}}</span>
      </template>
      <template :slot="`tab-print-header-${i}`">
        <div :class="['fr-print-header print-only',isPageTopCode(c)?'page-break-before':'',isUpdatingLayout?'calc-hidden':'']" :key="'ph-'+c">
          <div v-if="isPageTopCode(c)" class="school-name print-only">{{selectedSchoolDisplayName}}</div>
          <div class="mobile-view-spacer" :id="c"></div>
          <DivPrintableBackground :class="colorClassForCode(c)">
            <div class="section-name">{{tabNameForTabCode(c)}}</div>
          </DivPrintableBackground>
        </div>
        <div :class="['fr-responsive-header no-print',colorClassForCode(c)]" :key="'rh-'+c">
          <div class="v-outer">
            <div class="v-middle content">
              <div class="section-name">{{tabNameForTabCode(c)}}</div>
            </div>
          </div>
        </div>
      </template>

      <template :slot="`tab-content-${i}`">
        <div ref="scrollLocation" :class="['fr-tab-content',isPrintHideRRCode(c)?'print-hide-survey-rr':'']" :key="c">
          <ElementOverallRating
            v-if="c!=='INFO'"
            :element-code="c"
          />

          <div class="metric-columns-wrapper">

            <div v-for="colNum in layoutMetadataForTab(c)"
              :key="colNum.key"
              :class="['metric-column','col-'+colNum.key]"
            >
              <template  v-if="c==='INFO' && colNum.key==='1' && !isSelectedSchoolPK">
                <h3 class="group-heading">Overall School Ratings</h3>
                <FrameworkRatings/>
                <div class="legend-block">
                  <div class="key-title">Key:</div>
                  <FrameworkRatingsLegend/>
                </div>
              </template>
              <div v-for="group in colNum.values"
                :key="group.key"
                :class="['metric-group',group.key]"
              >
                <template v-for="r in group.values">
                  <component :key="r.key"
                    v-if="isValidComponentType(r.values[0].component_type)"
                    :is="r.values[0].component_type"
                    :metadata="r.values"
                    :is-calculating-layout="isUpdatingLayout"
                  />
                  <div v-else :key="r.key">Unknown component type: {{r.values[0].component_type}}</div>
                </template>
              </div>
            </div>
            <!-- This can be removed if the new survey-viz module will be a permanent addition -->
            <!-- Assumes TR section only has one column, duplicate footer here for printing only -->
            <!-- <div v-if="c=='TR'" class="metric-column col-2">
              <ReportFooter ref="print-footer" :class="['print-only',isUpdatingLayout?'calc-hidden':'']"/>
            </div> -->
          </div>
        </div>
      </template>
    </template>
  </TabsContainer>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'

import { colorClassForCode } from '@/utils/data'
import { SQS_PROJ_GRAD_RATE_YEAR } from '@/report-settings'
import { logEvent } from '@/helpers/google-analytics'
import { REPORT_YEAR, NS_CURRENT_REPORT } from './const'

import TabsContainer from '@/components/TabsContainer'
import DivPrintableBackground from '@/components/DivPrintableBackground'
import ReportFooter from './ReportFooter'
import FrameworkRatings from './FrameworkRatings.vue'
import FrameworkRatingsLegend from './FrameworkRatingsLegend'

import {
  ElementOverallRating,
  GroupHeading,
  SubHeading,
  Description,
  DescriptionNoSR,
  DescriptionSafe,
  ParagraphBreak,
  MetricSchoolOverview,
  MetricDescColon,
  MetricBignum,
  MetricSmallnum,
  MetricSmallnumD75,
  MetricCharterAuthorizer,
  MetricSchoolMap,
  MetricRatingGroup,
  MetricRatingGroupPkComp,
  MetricRatingGrid,
  MetricSurveyRR,
  MetricValueList,
  MetricValueListCount,
  MetricValueListNotSafe,
  MetricVizCompBars,
  MetricVizCompBarsInfo,
  MetricVizCompBarsSurvey,
  MetricThenNow,
  MetricAltAssess
} from './tab-mods'

/* For custom layout of text in buttons to determine line breaks,
  join without space for regular text due to SF */
const frElementNameLookupByLine = {
  // The INFO tab display string needs to align with the tabNameForCode in ReportMenu.vue
  'INFO': ['School ', 'Description'],
  // added for 2024
  'IP': ['Instruction and ', 'Performance'],
  'SS': ['Safety and ', 'School Climate'],
  'RF': ['Relationships ', 'with Families']
  // existed before 2024
  // 'SA': ['Student ', 'Achievement'],
  // 'RI': ['Rigorous ', 'Instruction'],
  // 'CT': ['Collaborative ', 'Teachers'],
  // 'SE': ['Supportive ', 'Environment'],
  // 'ES': ['Effective School ', 'Leadership'],
  // 'SF': ['Strong Family-', 'Community Ties'],
  // 'TR': ['Trust']
}

@Component({
  computed: {
    ...mapGetters(NS_CURRENT_REPORT, [
      'isEmbargoed',
      'selectedSchoolType',
      'isSelectedSchoolCharter',
      'isSelectedSchoolPK',
      'isSelectedSchoolDistrictPK',
      'selectedSchoolData',
      'layoutMetadataForSchoolType',
      'selectedSchoolDisplayName',
      'hasQRReview'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, {
      tabCodes: 'selectedSchoolTabCodes'
    })
  },
  methods: {
    colorClassForCode
  },
  components: {
    TabsContainer,
    DivPrintableBackground,
    ReportFooter,
    FrameworkRatings,
    FrameworkRatingsLegend,
    ElementOverallRating,
    GroupHeading,
    SubHeading,
    Description,
    DescriptionNoSR,
    DescriptionSafe,
    ParagraphBreak,
    MetricSchoolOverview,
    MetricDescColon,
    MetricBignum,
    MetricSmallnum,
    MetricSmallnumD75,
    MetricCharterAuthorizer,
    MetricSchoolMap,
    MetricRatingGroup,
    MetricRatingGroupPkComp,
    MetricRatingGrid,
    MetricSurveyRR,
    MetricValueList,
    MetricValueListCount,
    MetricValueListNotSafe,
    MetricVizCompBars,
    MetricVizCompBarsInfo,
    MetricVizCompBarsSurvey,
    MetricThenNow,
    MetricAltAssess
  }
})
export default class FrameworkDetails extends Vue {
  loading = true // true on startup, or when layout changes
  changingSchool = false
  isProjectedGradRate = REPORT_YEAR === SQS_PROJ_GRAD_RATE_YEAR

  get pageTopCodes () {
    return ['IP', 'SS', 'RF']
  }
  get printHideRRCodes () {
    // not hiding survey-rr for any school type for now
    return []
  }
  selectedTabCode = '' // invalid tab will default to first tab

  /* Lifecycle */

  loadTabHash () {
    const hashString = window.location.hash.substr(1)
    const hashIndex = this.tabCodes.indexOf(hashString)
    if (hashIndex > -1) {
      this.selectedTabCode = hashString
    }
  }

  mounted () {
    this.loading = false
    this.loadTabHash()
    window.onhashchange = this.loadTabHash
    this.trackMetaLinks()
  }

  updated () {
    /* Removed as part of the removal of the hidden footer second-column in Trust section */
    // if (this.changingSchool) {
    //   /* Only update size of hidden footer for printing when data changes,
    //     not when visibility changes */
    //   this.$refs['print-footer'][0].updateBGSize()
    // }
    this.loading = false
    this.changingSchool = false
    this.trackMetaLinks()
  }

  /* Watchers */

  @Watch('layoutMetadata')
  onLayoutMetadataChange (newVal, oldVal) {
    this.loading = true
  }
  @Watch('selectedSchoolData')
  onSchoolDataChange (newVal, oldVal) {
    this.changingSchool = true
  }

  /* Getters */

  get showProjGradDisclaimer () {
    // No need to show disclaimer for unofficial since grad rate is suppressed
    // currently isProjectedGradRate is true but isEmbargoed is set to true so the disclaimer note would not be displayed
    return !this.isEmbargoed && this.isProjectedGradRate
  }

  get selectedTabIndex () {
    // Default to first tab in case selected tab (e.g. SA) is no longer valid
    // for newly selected school type
    const tabIndex = this.tabCodes.indexOf(this.selectedTabCode)
    return tabIndex > -1 ? tabIndex : 0
  }

  get isUpdatingLayout () { return this.loading || this.changingSchool }

  get tabContainerClassesStr () {
    const classes = []
    if (this.selectedSchoolType) classes.push('st-' + this.selectedSchoolType.toLowerCase())
    if (this.isSelectedSchoolDistrictPK) classes.push('pk-district')
    if (this.isSelectedSchoolCharter) classes.push('charter')
    if (!this.hasQRReview) classes.push('no-qr')
    return classes.join(' ')
  }

  get tabButtonIds () { return this.tabCodes.map(c => this.tabButtonIdForCode(c)) }
  get tabContentIds () { return this.tabCodes.map(c => this.tabContentIdForCode(c)) }
  get tabButtonClasses () { return this.tabCodes.map(c => colorClassForCode(c)) }
  get tabContentClasses () { return this.tabCodes.map(c => 'print-always') }

  get layoutMetadata () {
    return this.layoutMetadataForSchoolType(this.selectedSchoolType)
  }
  get validComponentTypes () {
    let r = {}
    Object.assign(r, this.$options.components)
    return r
  }

  /* Event Handlers */

  onTabSelected (i) {
    this.selectedTabCode = this.tabCodes[i]
    window.location.hash = this.tabCodes[i]
    // scrolls the screen to be at the beginning of Tab content when you are past the content
    Vue.nextTick().then(() => {
      const element = this.$refs['scrollLocation'][i]
      // 125 is to move under the sticky top menu
      const top = element.offsetTop - 125
      if (top - window.scrollY < 0) {
        window.scrollTo(0, top)
      }
    })
  }

  /* Methods - utility */

  trackMetaLinks () {
    var links = document.querySelectorAll('.meta-link')
    links = Array.prototype.slice.call(links)
    links.forEach(function (e) {
      e.onclick = function () {
        logEvent(Vue, 'Link', 'linkClicked', e.href)
      }
    })
  }

  isValidComponentType (t) {
    return this.validComponentTypes.hasOwnProperty(t)
  }
  tabNameForTabCode (c) {
    const lines = frElementNameLookupByLine[c]
    return lines ? lines.join('') : ''
  }
  tabButtonLabelForTabCodeLine (c, i) {
    // i is line number indexed beginning with 1
    const lines = frElementNameLookupByLine[c]
    return lines && lines.length >= i ? lines[i - 1] : ''
  }
  tabButtonIdForCode (c) { return 'tab-button-' + c.toLowerCase() }
  tabContentIdForCode (c) { return 'tab-content-' + c.toLowerCase() }
  layoutMetadataForTab (c) { return this.layoutMetadata[c] || {} }
  isPageTopCode (c) { return this.pageTopCodes.indexOf(c) > -1 }
  isPrintHideRRCode (c) { return this.printHideRRCodes.indexOf(c) > -1 }
}
</script>

<style lang="scss">
</style>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import './css/const.scss';
$dark-blue: #1F497D;
/* below has the same value as the name-margin-left-big from FrameworkRatings*/
$legend-name-margin-left-big: 18px;

.framework-details {
  background-color: white;
  box-shadow: 0 4px 5px #bbbbbb;
  margin-bottom: 15px;
  /deep/ .tab-button-bar {
    justify-content: left;
    position: sticky;
    top: 46px;
    z-index: 1;
    background-color: $button-footer-header-bg;
  }
  /deep/ .tab-button {
    flex: 0 1 auto;
    font-size: 15px;
    padding: 8px 3px 10px 3px;
    border-width: 2px 0px 0px 0px;
    border-color: black;
    border-style: solid;
    background-color: white;
    box-shadow: 0px 1px 0px white;
    &:not(.selected):hover {
      color: #333;
      box-shadow: none;
    }
    &:not(.selected):not(:hover) {
      border-color: transparent;
      background-color: transparent;
      box-shadow: none;
    }
    &:not(:last-of-type) {
      margin-right: 50px;
    }
  }
  /deep/ .tab-content {
    padding: 0px 0px 10px 0px;
    margin: 0px 24px 0px 24px;
    @media print {
      padding-bottom: 0px;
    }
  }
  /deep/ .tab-print-header {
    padding: 0px;
    .school-name {
      font-size: 12px;
      padding: 8px;
    }
    .v-middle.content {
      .section-name {
        color: black;
      }
      border: 1px solid #777777;
    }
    .div-printable-bg {
      height: 48px;
      border-radius: 8px;
      background-color: $button-footer-header-bg;
      fill: $button-footer-header-bg;
      border-color: $button-footer-header-bg;
    }
  }
}
.color-info {
  background-color: #777777;
  border-top-color: #777777;
}
.section-name {
  width: 100%;
  padding: 0px 10px;
  font-size: 24px;
  font-weight: bold;
  color: white;

  /* This fixes name sticking out to right, but printing no longer fits? */
  box-sizing: border-box;
}
.fr-responsive-header {
  height: 34px;
  border-radius: 8px;
  .section-name {
    font-size: 16px;
    padding: 0px 10px;
  }
}
.tab-button-label {
  display: block;
  padding-left: 10px;
  padding-right: 10px;
}

.metric-columns-wrapper {
  display: flex;
  justify-content: space-between;

  /deep/ .ratings-content .viz-four-bar-rating.small {
    .bar {
      fill: #333333;
      &.hollow {
        fill: white;
      }
    }
  }
}
.metric-column {
  /* Fixed 24px between two columns */
  width: calc(50% - 12px);
  min-width: $min-width-col;
  .legend-block {
    display: flex;
    margin-left: $legend-name-margin-left-big;
  }
  .key-title {
    color: #767676;
    font-weight: bold;
    font-size: 15px;
    margin-top: 2px; // to align with the top margin of each 4-box legend
  }
}

/* Customization of behavior sub-components, including based on metric group */

#tab-content-info {
  .metric-group.gen {
    .description {
      padding-left: 2*$info-desc-indent;
      text-indent: -$info-desc-indent;
      // to handle when school website url is too long and overlapped with right column content (for example: 84K680)
      // source: https://css-tricks.com/snippets/css/prevent-long-urls-from-breaking-out-of-container/
      /deep/ a {
        overflow-wrap: break-word;
      }
    }
  }
  .metric-group.location,
  .metric-group.directory-overview {
    .sub-heading {
      padding-top: 20px;
    }
    .metric-school-map,
    .description {
      padding-left: $info-desc-indent;
    }
  }

  .metric-group.sch-demog {
    .sub-heading:not(:first-of-type) {
      padding-top: 20px;
    }
    .description.metric-colon {
      padding-left: 2*$info-desc-indent;
      text-indent: -$info-desc-indent;
    }
    /deep/ .viz-comp-bars {
      .bar.school-all {
        fill: $dark-blue;
      }
      .bar.boro-dist {
        fill: #437157;
      }
      .bar.neighborhood {
        fill: #399DAE;
      }
    }
    /deep/ .viz-legend {
      .legend-group .bar.school-all {
          border-left-color: $dark-blue;
      }
      .legend-group .bar.boro-dist {
          border-left-color: #437157;
      }
      .legend-group .bar.neighborhood {
          border-left-color: #399DAE;
      }
    }
  }

  .metric-group.program {
    .sub-heading:not(:first-of-type) {
      padding-top: 20px;
    }
    // only applies to D75 cuz only D75 schools has metric-value-list for Classroom Settings
    .metric-value-list {
      margin-block-start: 0;
      margin-block-end: 0;
    }
    .metric-viz-comp-bars.survey {
      margin-bottom: 0;
    }
    // only for the sports values
    .description.metric-colon {
      padding-left: 2*$info-desc-indent;
      text-indent: -$info-desc-indent;
      // this is a quick fix for the PSAL Sports text sticking out issue: https://doeord.atlassian.net/browse/SQRT-7821
      word-break: break-word;
    }
  }

  .metric-group.staff {
    .sub-heading:not(:first-of-type) {
      padding-top: 20px;
    }
    .description.metric-colon {
      padding-left: 2*$info-desc-indent;
      text-indent: -$info-desc-indent;
    }
  }

  .metric-group.space {
    .sub-heading:not(:first-of-type) {
      padding-top: 20px;
    }
  }

  .metric-group.survey-rr {
    /deep/ .response-group {
      padding-left: 2*$info-desc-indent;
      text-indent: -$info-desc-indent;
    }
  }
}

// For D75 school only
.framework-details.st-d75 {
  .metric-group.perf {
    .description {
      margin-bottom: 16px;
    }
  }
}

// For HS and HST schools starting at 2025 adding renamed Liv Environment metric to one line needs more space
.framework-details.st-hs,
.framework-details.st-hst {
  .metric-group.prog-to-grad {
    /deep/ .metric-rating-group {
      .description.rating {
        width: 185px;
      }
    }
  }
}

#tab-content-ip {
  .metric-group.grad /deep/ .grad-rate-disclaimer {
    display: inline-block;
    font-style: italic;
    padding-bottom: 1em;
  }
  .metric-group.ccr .metric-value-list.no-values {
    display: none;
  }
  // comp bar sizing should only change in the graduation section
  .metric-group.grad {
    /deep/ .viz-legend {
      .legend-group .bar.comp {
        height: 0.4em;
      }
    }
  }
  .metric-group.adv-demog {
    .metric-value-list {
      /deep/ .value-row {
        .value {
          width: 100px;
        }
      }
    }
    /deep/ .viz-comp-bars .bar{
      &.main {
        fill: $dark-blue;
      }
      &.adv-courses {
        fill: #876B8A;
      }
      &.ap-ib-courses {
        fill: #6898B6;
      }
    }
    /deep/ .viz-legend {
      .legend-group .bar{
        &.main {
          border-left-color: $dark-blue;
        }
        &.adv-courses {
          border-left-color: #876B8A;
        }
        &.ap-ib-courses {
          border-left-color: #6898B6;
        }
      }
    }
  }
  // dev database for July release: 84K680 and 09X404 (advanced_courses_suppression = 1) would display the adv-footnote
  .metric-group.adv-footnote {
    .description {
      /deep/ .foot-note-12px {
        font-size: 12px;
        font-style: italic;
        color: #999999;
        padding-top: 6px;
      }
    }
  }
}

// right now isEmbargoed is true so grad disclaimer won't be displayed
.tabs-container:not(.is-proj-grad) #tab-content-ip {
  .metric-group.grad /deep/ .grad-rate-disclaimer {
    display: none;
  }
}

.fr-tab-content {
  .metric-group {
    // applies to all bignum in all tabs
    .metric-bignum /deep/ .school-value {
      padding-left: 0;
      text-align: right;
      &.is-na {
        padding-right: 5px;
      }
    }
  }
}

.metric-group .description {
  margin-bottom: 0px;
}

.metric-group.lre {
  .metric-rating-group {
    padding-bottom: 16px;
  }
}

/* Responsive rules */

@media screen and (max-width: $screen-width-2-column-large) {
  .metric-column {
    max-width: $max-width-2-col;
  }
}
// screen size <= 956px
@media screen and (max-width: $screen-width-2-column-small) {
  .framework-details {
    /deep/ .tab-content {
      margin: 0px 10px;
    }
    /deep/ .tab-button-bar {
      display: none;
    }
    .legend-block {
      display: block;
      margin-left: 0;
    }
  }
  /deep/ .tab-print-header.selected {
    display: block;
  }
  .mobile-view-spacer {
    height: 105px;
  }
  #tab-content-info .metric-group {
    &.gen .description {
      padding-left: $info-desc-indent;
      text-indent: -$info-desc-indent;
    }
    &.sch-demog, &.program, &.staff {
      .description.metric-colon {
        padding-left: $info-desc-indent;
        text-indent: -$info-desc-indent;
      }
    }
  }
}
// change to 1 column view when screen size is <= 644px
@media screen and (max-width: $screen-width-1-column-large) {
  .framework-details {
    .legend-block {
      display: flex;
      margin-left: $legend-name-margin-left-big;
    }
  }
  .element-overall-rating,
  .metric-column {
    width: 100%;
    max-width: $max-width-1-col;
    margin: 0px auto;
  }
  /deep/ .viz-comp-bars {
    max-width: $max-width-2-col;
  }
  .metric-columns-wrapper {
    display: block;
  }
  #tab-content-info .metric-group{
    &.gen .description {
      padding-left: 2*$info-desc-indent;
      text-indent: -$info-desc-indent;
    }
    &.sch-demog, &.program, &.staff {
      .description.metric-colon {
        padding-left: 2*$info-desc-indent;
        text-indent: -$info-desc-indent;
      }
    }
  }
}
// screen size <= 538px
@media screen and (max-width: $screen-width-1-column-medium) {
  .framework-details {
    /deep/ .tab-content {
      margin: 0px 10px;
    }
  }
}
// screen size <= 456px
@media screen and (max-width: $screen-width-1-column-small) {
  #tab-content-info .metric-group{
    &.gen .description {
      padding-left: $info-desc-indent;
      text-indent: -$info-desc-indent;
    }
    &.sch-demog, &.program, &.staff {
      .description.metric-colon {
        padding-left: $info-desc-indent;
        text-indent: -$info-desc-indent;
      }
    }
  }
  .framework-details {
    .legend-block {
      display: block;
      margin-left: 0;
    }
  }
}
/* Custom print rules */

@media print {
  .framework-details {
    box-shadow: none;
    /deep/ .metric-group {
      break-inside: avoid-page;
    }
  }
  .fr-tab-content.print-hide-survey-rr .survey-rr {
    display: none;
  }
  .section-name {
    padding: 0px 24px;
  }
}

</style>



// WEBPACK FOOTER //
// src/components/sqs/2025/FrameworkDetails.vue